import { cabinBaggage, checkInBaggage, secondBaggagePerTraveler } from '@eti/products';
import {
  AgeType,
  BaggageStatus,
  type BaggageStatus as BaggageStatusType,
  CabinBaggageStatus,
  type CabinBaggageStatus as CabinBaggageStatusType,
  PersonalItemStatus,
  TripType,
} from '@eti/schema-types';
import type {
  AvailableExtraProductsType,
  BoundsType,
  BoundType,
  CabinBaggageTripSummaryType,
  CheckedBaggageSummaryType,
  PersonalItemTripSummaryType,
  SecondBagTripSummaryType,
  TravelersType,
} from '../../../models';
import { getBoundTitle, getBoundType } from '../../../../../widgets/trip-details/utils/format';
import type { BaggageType, BoundBaggageInfoType, TravelerBaggageInfo } from './models';

const getBoundBaggagePerTraveler = (
  boundId: string,
  travelers: NonNullable<TravelersType>,
  cabinBaggageTripSummary: CabinBaggageTripSummaryType,
  checkedBaggageSummary: CheckedBaggageSummaryType,
  personalItemTripSummary: PersonalItemTripSummaryType,
  secondBagTripSummary: SecondBagTripSummaryType,
): TravelerBaggageInfo[] => {
  const getCabinBaggageOfTraveler = (travelerId: string) => {
    const cabinBaggageSummaryOfBound = cabinBaggageTripSummary?.cabinBaggageBoundSummaries?.find(
      (cabinBaggageOfBound) => cabinBaggageOfBound?.boundId === boundId,
    );
    const cabinBaggageTravelerDetails =
      cabinBaggageSummaryOfBound?.cabinBaggageTravelerDetails?.find(
        (travelerDetails) => travelerDetails?.travelerId === travelerId,
      );

    if (
      !cabinBaggageTravelerDetails?.cabinBaggageDetails ||
      (!cabinBaggageTravelerDetails.cabinBaggageDetails.pieces &&
        !(
          [
            CabinBaggageStatus.AddedCabinBaggage,
            CabinBaggageStatus.IncludedCabinBaggage,
            CabinBaggageStatus.IncludedAndAddedCabinBaggage,
          ] as CabinBaggageStatusType[]
        ).includes(cabinBaggageTravelerDetails.cabinBaggageStatus))
    ) {
      return null;
    }

    return {
      pieces: cabinBaggageTravelerDetails.cabinBaggageDetails.pieces || 1,
      weight: cabinBaggageTravelerDetails.cabinBaggageDetails.weight || 0,
      weightUnit: cabinBaggageTravelerDetails.cabinBaggageDetails.weightUnit || 'kg',
      size: cabinBaggageTravelerDetails.cabinBaggageDetails.size3d,
    };
  };

  const getCheckedBaggageOfTraveler = (travelerId: string) => {
    const checkedBaggageSummaryOfBound = checkedBaggageSummary?.boundSummaries?.find(
      (checkedBaggageOfBound) => checkedBaggageOfBound?.boundId === boundId,
    );
    const checkedBaggageTravelerDetails = checkedBaggageSummaryOfBound?.summaries?.find(
      (travelerDetails) => travelerDetails?.travelerId === travelerId,
    );

    if (
      !checkedBaggageTravelerDetails?.baggageDetails ||
      (!checkedBaggageTravelerDetails.baggageDetails.pieces &&
        !(
          [
            BaggageStatus.AddedCheckedBaggage,
            BaggageStatus.IncludedCheckedBaggage,
          ] as BaggageStatusType[]
        ).includes(checkedBaggageTravelerDetails.baggageStatus))
    ) {
      return null;
    }

    return {
      pieces: checkedBaggageTravelerDetails.baggageDetails.pieces || 1,
      weight: checkedBaggageTravelerDetails.baggageDetails.weight || 0,
      weightUnit: checkedBaggageTravelerDetails.baggageDetails.weightUnit || 'kg',
    };
  };

  const getPersonalItemOfTraveler = (travelerId: string) => {
    const personalItemSummaryOfBound = personalItemTripSummary?.personalItemBoundSummaries?.find(
      (personalItemOfBound) => personalItemOfBound?.boundId === boundId,
    );
    const personalItemTravelerDetails =
      personalItemSummaryOfBound?.personalItemTravelerDetails?.find(
        (travelerDetails) => travelerDetails?.travelerId === travelerId,
      );

    if (
      personalItemTravelerDetails?.personalItemStatus !== PersonalItemStatus.IncludedPersonalItem
    ) {
      return null;
    }

    return {
      pieces: 1,
    };
  };

  const getSecondBaggageOfTraveler = (travelerId: string) => {
    const secondBaggageTravelerDetails = secondBagTripSummary?.secondBagSummaries?.find(
      (travelerDetails) => travelerDetails?.travelerId === travelerId,
    );

    if (!secondBaggageTravelerDetails || !secondBaggageTravelerDetails.baggageDetails.pieces) {
      return null;
    }

    return {
      pieces: secondBaggageTravelerDetails.baggageDetails.pieces,
      weight: secondBaggageTravelerDetails.baggageDetails.weight || 0,
      weightUnit: secondBaggageTravelerDetails.baggageDetails.weightUnit || 'kg',
    };
  };

  return travelers.reduce((acc: TravelerBaggageInfo[], traveler) => {
    if (traveler !== null && traveler.id && traveler.name) {
      const isInfant = traveler.ageType === AgeType.Inf;
      const secondBaggage = isInfant ? null : getSecondBaggageOfTraveler(traveler.id);
      const checkedBaggage = isInfant ? null : getCheckedBaggageOfTraveler(traveler.id);
      const cabinBaggageProduct = isInfant ? null : getCabinBaggageOfTraveler(traveler.id);
      const personalItem = isInfant ? null : getPersonalItemOfTraveler(traveler.id);
      const checkedBaggageToDisplay = checkedBaggage;
      let secondBaggageToDisplay = secondBaggage;

      if (
        checkedBaggageToDisplay &&
        secondBaggage &&
        secondBaggage.weight === checkedBaggage.weight &&
        secondBaggage.weightUnit === checkedBaggage.weightUnit
      ) {
        checkedBaggageToDisplay.pieces = checkedBaggage.pieces + secondBaggage.pieces;
        secondBaggageToDisplay = null;
      }

      acc.push({
        id: traveler.id,
        name: traveler.name,
        ageType: traveler.ageType,
        cabinBaggage: cabinBaggageProduct,
        checkedBaggage: checkedBaggageToDisplay,
        personalItem,
        secondBaggage: secondBaggageToDisplay,
      });
    }

    return acc;
  }, []);
};

const getBaggageItemWithChanges = (
  baggageItem: BaggageType | null,
  baggageItemToCompare: BaggageType | null,
) => {
  if (baggageItem === null) {
    return null;
  }

  return {
    ...baggageItem,
    hasChangesInPieces:
      baggageItem.hasChangesInPieces ||
      baggageItemToCompare === null ||
      baggageItemToCompare.hasChangesInPieces ||
      baggageItemToCompare.pieces !== baggageItem.pieces,
    hasChangesInWeight:
      baggageItem.hasChangesInWeight ||
      baggageItemToCompare === null ||
      baggageItemToCompare.hasChangesInWeight ||
      baggageItemToCompare.weight !== baggageItem.weight ||
      baggageItemToCompare.weightUnit !== baggageItem.weightUnit,
  };
};

const getTravelerBaggageInfoWithChanges = (
  travelerBaggageInfo: TravelerBaggageInfo,
  travelerBaggageInfoToCompare: TravelerBaggageInfo,
) => {
  return {
    ...travelerBaggageInfo,
    cabinBaggage: getBaggageItemWithChanges(
      travelerBaggageInfo.cabinBaggage,
      travelerBaggageInfoToCompare.cabinBaggage,
    ),
    checkedBaggage: getBaggageItemWithChanges(
      travelerBaggageInfo.checkedBaggage,
      travelerBaggageInfoToCompare.checkedBaggage,
    ),
    personalItem: getBaggageItemWithChanges(
      travelerBaggageInfo.personalItem,
      travelerBaggageInfoToCompare.personalItem,
    ),
    secondBaggage: getBaggageItemWithChanges(
      travelerBaggageInfo.secondBaggage,
      travelerBaggageInfoToCompare.secondBaggage,
    ),
  };
};

const getBoundBaggagePerTravelerWithChanges = (
  boundBaggagePerTraveler: TravelerBaggageInfo[],
  acc: BoundBaggageInfoType[],
) =>
  boundBaggagePerTraveler.map((travelerBaggageInfo: TravelerBaggageInfo, travelerIndex: number) => {
    // Update first bound baggage to contain the differences between first and current bound baggage
    acc[0].baggageInfoPerTraveler[travelerIndex] = getTravelerBaggageInfoWithChanges(
      acc[0].baggageInfoPerTraveler[travelerIndex],
      travelerBaggageInfo,
    );

    // Update current bound baggage to contain the differences between first and current bound baggage
    return getTravelerBaggageInfoWithChanges(
      travelerBaggageInfo,
      acc[0].baggageInfoPerTraveler[travelerIndex],
    );
  });

const getBoundOrigin = (bound: BoundType) => {
  const firstSegment = bound?.segments[0];
  return firstSegment?.__typename === 'TripSegment' ? firstSegment.origin.cityName : null;
};

const getBoundDestination = (bound: BoundType) => {
  const lastSegment = bound?.segments[bound?.segments.length - 1];
  return lastSegment?.__typename === 'TripSegment' ? lastSegment.destination.cityName : null;
};

export const getTransformedBaggagePerBound = (
  bounds: NonNullable<BoundsType>,
  travelers: NonNullable<TravelersType>,
  cabinBaggageTripSummary: CabinBaggageTripSummaryType,
  checkedBaggageSummary: CheckedBaggageSummaryType,
  personalItemTripSummary: PersonalItemTripSummaryType,
  secondBagTripSummary: SecondBagTripSummaryType,
  journeyType: TripType,
): BoundBaggageInfoType[] => {
  return bounds.reduce((acc: BoundBaggageInfoType[], bound, boundIndex) => {
    if (bound?.boundId) {
      const boundTitle = getBoundTitle(
        getBoundType(boundIndex),
        journeyType === TripType.MultiStop,
      );
      const origin = getBoundOrigin(bound);
      const destination = getBoundDestination(bound);
      const boundBaggagePerTraveler = getBoundBaggagePerTraveler(
        bound.boundId,
        travelers,
        cabinBaggageTripSummary,
        checkedBaggageSummary,
        personalItemTripSummary,
        secondBagTripSummary,
      );

      const boundBaggagePerTravelerWithChanges =
        boundIndex > 0
          ? getBoundBaggagePerTravelerWithChanges(boundBaggagePerTraveler, acc)
          : boundBaggagePerTraveler;

      if (origin !== null && destination !== null) {
        acc.push({
          boundId: bound.boundId,
          boundTitle,
          baggageInfoPerTraveler: boundBaggagePerTravelerWithChanges,
          destination,
          origin,
        });
      }
    }
    return acc;
  }, []);
};

export const hasDifferencesBetweenBounds = (transformedBaggagePerBound: BoundBaggageInfoType[]) =>
  transformedBaggagePerBound.some((boundBaggage) =>
    boundBaggage.baggageInfoPerTraveler.some(
      (travelerBaggage) =>
        travelerBaggage.cabinBaggage?.hasChangesInPieces ||
        travelerBaggage.cabinBaggage?.hasChangesInWeight ||
        travelerBaggage.checkedBaggage?.hasChangesInPieces ||
        travelerBaggage.checkedBaggage?.hasChangesInWeight ||
        travelerBaggage.personalItem?.hasChangesInPieces ||
        travelerBaggage.secondBaggage?.hasChangesInPieces ||
        travelerBaggage.secondBaggage?.hasChangesInWeight,
    ),
  );

export const getBaggageIdsAvailableToBuy = (
  availableExtraProducts: NonNullable<AvailableExtraProductsType>,
): string[] =>
  availableExtraProducts.reduce((acc: string[], product) => {
    if (
      product?.id &&
      (Number(product.id) === cabinBaggage.id ||
        Number(product.id) === checkInBaggage.id ||
        Number(product.id) === secondBaggagePerTraveler.id)
    ) {
      acc.push(product.id);
    }
    return acc;
  }, []);
